/**
 * ScenePreview — Date picker + preview button, shows matched scene
 */

import { useState } from 'react';
import { Search } from 'lucide-react';
import { welcomeApi } from '@/lib/api/welcome';
import type { PreviewResult } from '@/lib/api/welcome';
import { Button } from '@/components/ui/Button';

export function ScenePreview() {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePreview = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await welcomeApi.preview(date);
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '预览失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
      <h3 className="text-sm font-semibold text-gray-700">场景预览</h3>
      <div className="flex gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <Button size="sm" onClick={handlePreview} loading={loading}>
          <Search className="w-4 h-4 mr-1" />
          预览
        </Button>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
      )}

      {result && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">匹配场景:</span>
            <span className="font-medium text-gray-900">
              {result.matched_scene ?? '未匹配'}
            </span>
            {result.is_default && (
              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">默认</span>
            )}
          </div>
          {result.scene_key && (
            <div className="flex items-center gap-2">
              <span className="text-gray-500">场景 Key:</span>
              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">
                {result.scene_key}
              </code>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-gray-500">文案数量:</span>
            <span className="font-medium">{result.titles_count}</span>
          </div>
        </div>
      )}
    </div>
  );
}
